import { useEffect, useState } from 'react';
import { supervisorBase } from '../utils/georgeUrl';

// Map of agent id -> last agent metadata version the user has seen.
export type SeenUpdatesMap = Record<string, string>;

// Module-level cache keyed by access token so React StrictMode double-mounts and
// re-renders don't refetch; `inFlightPromise` deduplicates concurrent fetches.
let cachedResult: SeenUpdatesMap | null = null;
let cachedToken: string | null = null;
let inFlightPromise: Promise<SeenUpdatesMap> | null = null;
let inFlightToken: string | null = null;

// The supervisor verifies identity on the ID token — the org AS's access
// token carries no `groups` claim to authorize against — and still forwards
// the bearer token downstream to the sub-agents it aggregates.
function authHeaders(accessToken: string, idToken: string): Record<string, string> {
  return { Authorization: `Bearer ${accessToken}`, 'X-Id-Token': idToken };
}

async function fetchSeenUpdates(accessToken: string, idToken: string): Promise<SeenUpdatesMap> {
  const response = await fetch(`${supervisorBase()}/api/v1/seen-updates`, {
    headers: authHeaders(accessToken, idToken),
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return (await response.json()) as SeenUpdatesMap;
}

async function postSeenUpdate(
  accessToken: string,
  idToken: string,
  agentId: string,
  version: string
): Promise<SeenUpdatesMap> {
  const response = await fetch(`${supervisorBase()}/api/v1/seen-updates`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(accessToken, idToken),
    },
    body: JSON.stringify({ agent_id: agentId, version }),
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return (await response.json()) as SeenUpdatesMap;
}

export function useSeenUpdates(accessToken: string, idToken: string) {
  const initial: SeenUpdatesMap | null =
    cachedResult && cachedToken === accessToken ? cachedResult : null;
  const [seen, setSeen] = useState<SeenUpdatesMap>(initial ?? {});
  // `loaded` distinguishes "still fetching" from "fetched, agent simply absent"
  // (a first-ever visit). Callers must not decide badge visibility until true.
  const [loaded, setLoaded] = useState<boolean>(initial !== null);

  useEffect(() => {
    if (!accessToken) return;

    if (cachedResult && cachedToken === accessToken) {
      setSeen(cachedResult);
      setLoaded(true);
      return;
    }

    if (!inFlightPromise || inFlightToken !== accessToken) {
      inFlightToken = accessToken;
      inFlightPromise = fetchSeenUpdates(accessToken, idToken)
        .catch(() => ({}) as SeenUpdatesMap)
        .finally(() => {
          inFlightPromise = null;
          inFlightToken = null;
        });
    }

    void inFlightPromise.then((result) => {
      cachedResult = result;
      cachedToken = accessToken;
      setSeen(result);
      setLoaded(true);
    });
  }, [accessToken, idToken]);

  // Optimistically mark a version as seen locally and persist it. The badge is
  // non-critical, so a failed POST is swallowed (local state still clears it).
  const markSeen = (agentId: string, version: string) => {
    if (!version || seen[agentId] === version) return;
    const next = { ...seen, [agentId]: version };
    setSeen(next);
    if (cachedToken === accessToken) cachedResult = next;
    if (!accessToken) return;
    void postSeenUpdate(accessToken, idToken, agentId, version)
      .then((result) => {
        cachedResult = result;
        cachedToken = accessToken;
        setSeen(result);
      })
      .catch(() => undefined);
  };

  // Establish a baseline for agents the user has never seen (first-ever visit):
  // record their current versions WITHOUT ever showing a "New" badge. Persists
  // each in the background; local state updates immediately so no badge flashes.
  const markSeenSilently = (versions: Record<string, string>) => {
    const pending = Object.entries(versions).filter(
      ([id, version]) => version && seen[id] === undefined
    );
    if (pending.length === 0) return;

    const next = { ...seen };
    for (const [id, version] of pending) next[id] = version;
    setSeen(next);
    if (cachedToken === accessToken) cachedResult = next;

    if (!accessToken) return;
    for (const [id, version] of pending) {
      void postSeenUpdate(accessToken, idToken, id, version).catch(() => undefined);
    }
  };

  return { seen, loaded, markSeen, markSeenSilently };
}
